/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo } from 'react';
import { WIDGETS } from '/widgets';
import { LAYOUT_OPTIONS } from './layoutOptions';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import isEmpty from 'lodash-es/isEmpty';
import AppCard, { cardStyle } from 'views/explorer/cardWrapper';
import { useIsSavedStateAvailable } from 'hooks/useApplicationState';
import { Card } from '@blueprintjs/core';
import { ColorScalesContext } from 'views/common/colorScalesContext';
import { useDefaultColors } from 'utilities/setUpColorScale';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';

/**
 * Support the layout of multiple ExplorerCards.  This manager class accesses
 * the context and acts upon the following pieces of state:
 *
 * {
 *   activeState: {
 *     explorer: {
 *       layoutStyleKey: "..",
 *       currentCards:[..]
 *     }
 *   },
 *   savedStates: {
 *     widgets:[..]
 *   }
 * }
 *
 * @returns {*}
 * @constructor
 */
export const ExplorerLayoutManager = ({ layoutOptions = undefined, showAllSavedWidgets = false }) => {
	const {
		state: {
			explorer: { layout, selectedDefinitionName },
			transient: { widgets },
		},
		dispatch
	} = useContext(WorkspaceContext);

	const isSavable = useIsSavedStateAvailable();

	const layoutStyle = layoutOptions ? layoutOptions[layout.layoutStyle.key] : LAYOUT_OPTIONS[layout.layoutStyle.key];
	const styles = layoutStyle?.styles;

	// Only render widgets that are used by this application
	// This needs to update the transient widgets, which should be used to create the correct
	// cards.  This is needed to support the project specific widget ordering.
	const availableWidgets = useMemo(() => {
		return widgets
			.map((widget) => WIDGETS[widget.key])
			.filter((widget) => typeof widget.isValid === 'function' && widget.isValid(selectedDefinitionName));
	}, [widgets, selectedDefinitionName]);

	//Pick up any app specific colors from up the tree, if they exist use them, if not set up slykit default colors
	const appSpecificColors = useContext(ColorScalesContext);
	const getColorScale = useDefaultColors(appSpecificColors);

	// reset the layout if there's an invalid widget selected 
	// helps refresh the explorer when swapping between different dil definitions with different widgets available
	useEffect(() => {
		if (layout.cards.filter(card => !availableWidgets.some(x => x.key === card.type)).length > 0) {
			dispatch(WorkspaceActionCreator.createNewExplorer());
		}
	}, [availableWidgets, dispatch, layout.cards]);

	if (isEmpty(availableWidgets)) {
		return <div />;
	}

	return (
		<ColorScalesContext.Provider value={getColorScale}>
			<div cy-test="widget-wrapper" css={[wrapperBaseStyle, styles]}>
				{layout.cards.map((card, i) => (
					<AppCard
						key={`${i}-${card.type}`}
						card={card}
						cardIndex={i}
						availableWidgets={availableWidgets}
						layoutOptions={layoutOptions}
						showAllSavedWidgets={showAllSavedWidgets}
					/>
				))}
				{layout.cards.length < 1 && isSavable && (
					<Card cy-test="widget-card" css={emptyWidget}>
						Select a Saved View
					</Card>
				)}
			</div>
		</ColorScalesContext.Provider>
	);
};

const wrapperBaseStyle = css`
	width: 100%;
	height: 100%;
	display: grid;
	grid-column-gap: 8px;
	grid-row-gap: 8px;
	// Ensure Layout Wrapper resizes
	padding: 1px; // So the box shadows still look good.
	& > * {
		overflow: hidden; // required to make things shrink properly.
	}
`;

const emptyWidget = css`
	${cardStyle};
	justify-content: center;
	text-align: center;
	font-weight: bold;
`;

ExplorerLayoutManager.propTypes = { result: PropTypes.any };
